import React from 'react';
import { useModulePermissions } from './usePermissionHook';

/**
 * Higher-Order Component for permission-based component wrapping
 * @param {React.Component} WrappedComponent - The component to wrap
 * @param {number|string} moduleId - The module ID for permission checking
 * @param {object} options - Configuration options
 * @returns {React.Component} Wrapped component with permission props
 */
export const withPermissions = (WrappedComponent, moduleId, options = {}) => {
  const {
    fallback = null,
    showWhenNoPermission = false,
    permissionProps = true,
    accessPermission,
  } = options;

  const WithPermissionsComponent = (props) => {
    const permissions = useModulePermissions(moduleId);
    
    // If user doesn't have access and we shouldn't show when no permission
    if (!permissions.hasAccess() && !showWhenNoPermission) {
      return fallback;
    }

    if (accessPermission && typeof permissions[accessPermission] === 'function' && !permissions[accessPermission]()) {
      return fallback;
    }

    // Prepare props to pass to the wrapped component
    const componentProps = {
      ...props,
      ...(permissionProps && {
        permissions,
        canVisible: permissions.canVisible(),
        canView: permissions.canView(),
        canAdd: permissions.canAdd(),
        canEdit: permissions.canEdit(),
        canDelete: permissions.canDelete(),
        canPrint: permissions.canPrint(),
        canExport: permissions.canExport(),
        canSameDateUpdate: permissions.canSameDateUpdate(),
        // CRUD operations
        canCreate: permissions.canCreate(),
        canRead: permissions.canRead(),
        canUpdate: permissions.canUpdate(),
        canDestroy: permissions.canDestroy(),
        hasAccess: permissions.hasAccess(),
      }),
    };

    return <WrappedComponent {...componentProps} />;
  };

  WithPermissionsComponent.displayName = `withPermissions(${WrappedComponent.displayName || WrappedComponent.name})`;
  
  return WithPermissionsComponent;
};

/**
 * Permission Guard Component for conditional rendering
 * @param {object} props - Component props
 * @param {number|string} props.moduleId - Module ID
 * @param {string} props.permission - Permission type to check
 * @param {React.ReactNode} props.children - Children to render if permission is granted
 * @param {React.ReactNode} props.fallback - Fallback content if permission is denied
 * @param {boolean} props.showWhenNoPermission - Whether to show children even without permission
 * @returns {React.ReactNode} Rendered content based on permissions
 */
export const PermissionGuard = ({ 
  moduleId, 
  permission, 
  children, 
  fallback = null, 
  showWhenNoPermission = false 
}) => {
  const permissions = useModulePermissions(moduleId);
  
  const hasPermission = permissions.hasPermission(moduleId, permission);
  
  if (!hasPermission && !showWhenNoPermission) {
    return fallback;
  }
  
  return children;
};

/**
 * Permission-based Button Component
 * @param {object} props - Component props
 * @param {number|string} props.moduleId - Module ID
 * @param {string} props.permission - Permission type to check
 * @param {React.ReactNode} props.children - Button content
 * @param {object} props.buttonProps - Props to pass to the button
 * @param {React.ReactNode} props.fallback - Fallback content if permission is denied
 * @returns {React.ReactNode} Button or fallback content
 */
export const PermissionButton = ({ 
  moduleId, 
  permission, 
  children, 
  buttonProps = {}, 
  fallback = null 
}) => {
  const permissions = useModulePermissions(moduleId);
  
  const hasPermission = permissions.hasPermission(moduleId, permission);
  
  if (!hasPermission) {
    return fallback;
  }
  
  return React.cloneElement(children, buttonProps);
};

/**
 * Permission-based Action Component for common CRUD operations
 * @param {object} props - Component props
 * @param {number|string} props.moduleId - Module ID
 * @param {React.ReactNode} props.children - Children to render
 * @param {object} props.permissions - Specific permissions to check
 * @param {React.ReactNode} props.fallback - Fallback content
 * @returns {React.ReactNode} Rendered content based on CRUD permissions
 */
export const CRUDPermissionGuard = ({ 
  moduleId, 
  children, 
  permissions = {}, 
  fallback = null 
}) => {
  const modulePermissions = useModulePermissions(moduleId);
  
  // Check if user has any of the required permissions
  const hasRequiredPermission = Object.keys(permissions).every(permission => {
    const required = permissions[permission];
    if (typeof required === 'boolean') {
      return required ? modulePermissions.hasPermission(moduleId, permission) : true;
    }
    return modulePermissions.hasPermission(moduleId, permission);
  });
  
  if (!hasRequiredPermission) {
    return fallback;
  }
  
  return children;
};

/**
 * Permission-based Table Actions Component
 * @param {object} props - Component props
 * @param {number|string} props.moduleId - Module ID
 * @param {object} props.record - Table record data
 * @param {function} props.onEdit - Edit handler
 * @param {function} props.onDelete - Delete handler
 * @param {function} props.onView - View handler
 * @param {function} props.onPrint - Print handler
 * @param {function} props.onExport - Export handler
 * @param {object} props.buttonProps - Props for action buttons
 * @returns {React.ReactNode} Action buttons based on permissions
 */
export const TableActionButtons = ({ 
  moduleId, 
  record, 
  onEdit, 
  onDelete, 
  onView, 
  onPrint, 
  onExport, 
  buttonProps = {} 
}) => {
  const permissions = useModulePermissions(moduleId);
  
  const actions = [];
  
  if (permissions.canView() && onView) {
    actions.push(
      <button 
        key="view" 
        onClick={() => onView(record)} 
        {...buttonProps}
      >
        View
      </button>
    );
  }
  
  if (permissions.canEdit() && onEdit) {
    actions.push(
      <button 
        key="edit" 
        onClick={() => onEdit(record)} 
        {...buttonProps}
      >
        Edit
      </button>
    );
  }
  
  if (permissions.canDelete() && onDelete) {
    actions.push(
      <button 
        key="delete" 
        onClick={() => onDelete(record)} 
        {...buttonProps}
      >
        Delete
      </button>
    );
  }
  
  if (permissions.canPrint() && onPrint) {
    actions.push(
      <button 
        key="print" 
        onClick={() => onPrint(record)} 
        {...buttonProps}
      >
        Print
      </button>
    );
  }
  
  if (permissions.canExport() && onExport) {
    actions.push(
      <button 
        key="export" 
        onClick={() => onExport(record)} 
        {...buttonProps}
      >
        Export
      </button>
    );
  }
  
  return <div className="table-actions">{actions}</div>;
}; 